import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {StateHandler} from "../../../../core/utils/StateHandler";
import {Presentation} from "../../../../core/gameflow/controller/Presentation";
import {GameData} from "../../../server/data/GameData";
import {InitData} from "../../../server/data/InitData";
import {PREngineManager} from "../../../punkEngine/PREngineManager";
import {SoundConfig} from "../../../sound/SoundConfig";
import {SoundManager} from "../../../sound/SoundManager";
import {PunkRocker} from "../../../PunkRocker";
import {MathHelper} from "../../../../core/utils/MathHelper";
import {IStickyJump, jumpingWildConfig} from "../../../server/base/IData";
import {JumpWildState} from "../../independent/jumpingWild/JumpingWildController";
import {gsap} from "gsap";
import {ArrayHelper} from "../../../../core/utils/ArrayHelper";
import {BigWinPosterState, PosterManager} from "../../independent/Poster/PosterManager";
import {PRGameMode} from "../../../server/PRGameMode";
import {TrainState} from "../../independent/trains/TrainState";
import {SpinePlayer} from "../../../../core/displayobject/SpinePlayer";
import {GameConfig} from "../../../config/base/GameConfig";

export class FeatureStopState extends StateHandler {
    public static readonly RESTORE: string = "restore";
    public static readonly RESPIN: string = GameMode.RESPIN;
    public static readonly TRAINSTOPS: string = "trainstops";
    public static readonly TRAINENTER: string = "trainenter";
    public static readonly FEATURES_TOP: string = "featuresTop";
    public static readonly FEATURES_RESET: string = "featuresReset";
    public static readonly FEATURES_BOTTOM: string = "featuresBottom";
    public static readonly DODARK: string = "dodark";
    public static readonly REMDARK: string = "remdark";
    public static readonly DEDARK: string = "dedark";
    public static readonly STOP: string = "stop";
    public static readonly DONE: string = "done";
}

interface featureHitData {
    jumpingWildIDs: number[];
    reelID: number;
    featureTriggered: string[];
}

export class FeatureReelStopPresentation extends Presentation {

    protected _bonusState: FeatureStopState = new FeatureStopState(FeatureStopState.STOP);
    protected jumpWReelPos: number[];
    protected _gameData: GameData;
    protected fIndex: number = 0;
    protected featureWonTopTrain: string[];
    protected featureWonBottomTrain: string[];
    protected restored: boolean = false;
    protected jwBefore: number = 0;
    protected fsUpdate: number = 0;
    protected fsLeft: number = 0;
    protected _jumpPositions: IStickyJump[] = [];
    private _featureData: featureHitData[] = [];
    private _is6x6Done: boolean = false;

    private _isSkipped: boolean = false;

    get isSkipped(): boolean {
        return this._isSkipped;
    }

    set isSkipped(value: boolean) {
        GameConfig.TRAIN_PRESENTATION_SKIPPED = value;
        this._isSkipped = value;
    }

    public setInitData(data: InitData) {
        this.restored = data.isRestoreState && ((data.nextMode === PRGameMode.FREESPIN_LEVEL_ONE) || (data.nextMode === PRGameMode.FREESPIN_LEVEL_TWO) || (data.nextMode === PRGameMode.FREESPIN_LEVEL_THREE));
        for (let s: number = 0; s < data.Reel.scatterPositions.length; s++) {
            if (data.Reel.scatterPositions[s] >= 0) {
                this.restored = false;
            }
        }
    }

    public setGamedData(data: GameData): void {
        this._gameData = data;
        this._jumpPositions = data.stickyJumps;
        this._bonusState.set(this._jumpPositions.length != 0 ? FeatureStopState.TRAINENTER : FeatureStopState.STOP);

        if (this.restored) {
            this._bonusState.set(FeatureStopState.RESTORE);
        }

        this.fsLeft = data.raw.freespinsLeft;
        this.fIndex = 0;
        this.featureWonTopTrain = data.raw.topTrain;
        this.featureWonBottomTrain = data.raw.bottomTrain;
        this.fsUpdate = data.raw.freespinsLeft;
        this.jwBefore = this._jumpPositions.length;
        this.jumpWReelPos = [];
        this._featureData = [];

        this._jumpPositions.forEach((conf: IStickyJump, jwID: number): void => {
            const featureReelId: number = conf.to.x;

            let dataExists: featureHitData | undefined = this._featureData.find(d => d.reelID == conf.to.x);

            if (dataExists) {
                if (!(dataExists.jumpingWildIDs.includes(jwID))) {
                    dataExists.jumpingWildIDs.push(jwID);
                }
            } else {
                this._featureData.push({
                    reelID: featureReelId,
                    jumpingWildIDs: [jwID],
                    featureTriggered: [this.featureWonTopTrain[featureReelId], this.featureWonBottomTrain[featureReelId]]
                });
            }
        });
        this._featureData.sort((a, b) => a.reelID - b.reelID);

        for (let k = 0; k < this._featureData.length; k++) {
            if (this._featureData[k].reelID > -1) {
                this.jumpWReelPos.push(this._featureData[k].reelID);
            } else {
                this.jumpWReelPos.push(this.featureWonTopTrain.indexOf("UPGRADE"));
            }
        }

        for (let fW: number = 0; fW < this.featureWonTopTrain.length; fW++) {
            if (this.jumpWReelPos.indexOf(fW) == -1) {
                continue;
            }

            if (this.featureWonTopTrain[fW] === "PLUS_1") {
                let amount: number = 0;
                for (let i: number = 0; i < this._featureData.length; i++) {
                    const currFD: featureHitData = this._featureData[i];

                    if (fW === currFD.reelID && (currFD.featureTriggered.includes("PLUS_1"))) {
                        amount = currFD.jumpingWildIDs.length;
                    }
                }
                this.fsUpdate -= amount;
            }

            if (this.featureWonTopTrain[fW] === "UPGRADE") {
                this.fsUpdate -= 2;
            }
        }

        for (let fW: number = 0; fW < this.featureWonBottomTrain.length; fW++) {
            if (this.jumpWReelPos.indexOf(fW) == -1) {
                continue;
            }

            if (this.featureWonBottomTrain[fW] === "PLUS_1") {
                let amount: number = 0;
                for (let i: number = 0; i < this._featureData.length; i++) {
                    const currFD: featureHitData = this._featureData[i];

                    if (fW === currFD.reelID && (currFD.featureTriggered.includes("PLUS_1"))) {
                        amount = currFD.jumpingWildIDs.length;
                    }
                }
                this.fsUpdate -= amount;
            }
        }
    }

    skip() {
        let continueSkip: boolean = true;

        if (this._bonusState.equal(FeatureStopState.FEATURES_TOP) || this._bonusState.equal(FeatureStopState.FEATURES_RESET)) {
            for (let i: number = 0; i < this._featureData.length; i++) {
                const currFeature: featureHitData = this._featureData[i];

                if (this._is6x6Done) {
                    break;
                }

                if (currFeature.featureTriggered.includes("6X6") && !(this._is6x6Done)) {
                    continueSkip = false;
                    break;
                }
            }
        }

        if (continueSkip) {
            this.isSkipped = true;
            gsap.globalTimeline.timeScale(20);
            super.skip();
        }
    }

    public createPresentation(enableSkip: Function) {
        enableSkip(this._bonusState.equal(FeatureStopState.TRAINENTER)
            || this._bonusState.equal(FeatureStopState.FEATURES_TOP)
            || this._bonusState.equal(FeatureStopState.FEATURES_BOTTOM)
        );

        // #310
        for (let reel: number = 0; reel < PREngineManager.AllReels.length; reel++) {
            PREngineManager.getReel(reel).overlayGraphicAlpha(0, 0.3);
            PREngineManager.getReel(reel).showReelGlow(false);
        }

        switch (this._bonusState.value) {
            case FeatureStopState.RESTORE:
                this.restoring();
                break;
            case FeatureStopState.TRAINENTER:
                this.trainArrives();
                PunkRocker.jumpWilds.reArrangeWilds();
                this.jumpTheWilds();
                this._bonusState.set(FeatureStopState.TRAINSTOPS);
                break;
            case FeatureStopState.TRAINSTOPS:
                this.stopTopTrain();
                this._bonusState.set(FeatureStopState.DODARK);
                break;
            case FeatureStopState.DODARK:
                this.glowReels("top");
                this._bonusState.set(FeatureStopState.REMDARK);
                break;
            case FeatureStopState.REMDARK:
                this.removeNonHits(0.5);
                this._bonusState.set(FeatureStopState.FEATURES_TOP);
                break;
            case FeatureStopState.FEATURES_TOP:
                const featureHitTopTrainData: featureHitData = this.getFeatureHitData();
                if (featureHitTopTrainData) {
                    if (featureHitTopTrainData.featureTriggered[0]) {
                        this.presentFeatureWon(featureHitTopTrainData, "top");
                    }
                    this.fIndex++;

                    if (this._featureData.length == this.fIndex) {
                        this._bonusState.set(FeatureStopState.FEATURES_RESET);
                    }
                } else {
                    this._bonusState.set(FeatureStopState.FEATURES_RESET);
                }

                break;
            case FeatureStopState.FEATURES_RESET:
                this.unGlowReels();
                this.stopBottomTrain();

                this.fIndex = 0;
                this._bonusState.set(FeatureStopState.FEATURES_BOTTOM);
                break;
            case FeatureStopState.FEATURES_BOTTOM:
                this.glowReels("bottom");
                const featureHitBottomTrainData: featureHitData = this.getFeatureHitData();
                if (featureHitBottomTrainData) {
                    if (featureHitBottomTrainData.featureTriggered[0]) {
                        this.presentFeatureWon(featureHitBottomTrainData, "bottom");
                    }
                    this.fIndex++;

                    if (this._featureData.length == this.fIndex) {
                        this._bonusState.set(FeatureStopState.DEDARK);
                    }
                } else {
                    this._bonusState.set(FeatureStopState.DEDARK);
                }
                break;

            case FeatureStopState.DEDARK:
                if (PunkRocker.model.gameData.raw.bottomTrainEnabled) {
                    this.unGlowReels();
                }

                //400 ms before the reels stop
                this.timeline.to(this, {
                    duration: 0.4
                });

                this._bonusState.set(FeatureStopState.STOP);
                break;
            default:
                this._is6x6Done = false;
                this.resetSkip();
                this.PresentationState.setInactive();
                break;
        }
    }

    protected getFeatureHitData(): featureHitData {
        return this._featureData[this.fIndex];
    }

    protected restoring(): void {
        if (this.restored) {
            PunkRocker.trainsManager.topTrain.setTrainItems(this.featureWonTopTrain);
            PunkRocker.trainsManager.bottomTrain.setTrainItems(this.featureWonBottomTrain);

            let reel = ArrayHelper.fetchLastValue(PREngineManager.AllReels);
            reel.isActive = true;
            reel.createStart();

            this.timeline.to(this, {
                duration: 0.3
            });

            SoundManager.playBonus();
            SoundManager.fadeAmbienceLoop(1, 1);

            this.timeline.add([
                () => reel.startReel(),
                () => {
                    PunkRocker.trainsManager.topTrain.setTrainAlpha(true, 0.2);
                    PunkRocker.trainsManager.bottomTrain.setTrainAlpha(true, 0.2);
                }
            ]);

            this.timeline.to(this, {
                duration: 0.3
            });

            this.restored = false;
            this.timeline.add(() => this._bonusState.set(FeatureStopState.TRAINENTER));
        }
    }

    protected removeNonHits(duration: number = 0.5): void {
        PREngineManager.RAll(r => {
            if (this._featureData.find((s) => s.reelID === r.reelID)) {
                if (this.featureWonTopTrain[r.reelID] === "BLANK" && this.featureWonBottomTrain[r.reelID] === "BLANK") {
                    this.timeline.add([
                        () => r.overlayGraphicAlpha(0.6, duration),
                        () => r.showReelGlow(false)
                    ], 0)
                }
            }
        });
        /*this.timeline.to(this, {duration: duration / 4}, 0);*/
    }

    protected glowReels(onTrain: string, duration: number = 1): void {
        PREngineManager.RAll(reel => {
            let flag: boolean = false;

            if (this.jumpWReelPos.includes(reel.reelID)) {
                flag = (onTrain === "top") ? (this.featureWonTopTrain[reel.reelID] !== "BLANK") : (this.featureWonBottomTrain[reel.reelID] !== "BLANK");
            }

            this.timeline.add(() => reel.overlayGraphicAlpha(flag ? 0 : 0.6, duration), 0);

            if (flag) {
                this.timeline.add(() => reel.showReelGlow(true, (onTrain + "Train")), 0);
            }
        });
        /*this.timeline.to(this, {
            duration: duration
        });*/
    }

    protected presentFeatureWon(conf: featureHitData, onTrain: string): void {
        if (onTrain === "bottom" && !(PunkRocker.model.gameData.raw.bottomTrainEnabled)) {
            return;
        }
        if (conf.reelID === -1) {
            conf.reelID = this.featureWonTopTrain.indexOf("UPGRADE");
        }

        let reelID: number = conf.reelID;
        let featureName: string = (onTrain === "top") ? conf.featureTriggered[0] : conf.featureTriggered[1];
        let jwIDs: number[] = conf.jumpingWildIDs;

        const durr: number = 1;

        let playAnimations: boolean = false;
        if (onTrain === "top" && this.featureWonTopTrain[conf.reelID] !== "BLANK") {
            playAnimations = true;
        }
        if (onTrain === "bottom" && this.featureWonBottomTrain[conf.reelID] !== "BLANK") {
            playAnimations = true;
        }

        if (playAnimations) {
            jwIDs.forEach(id => {
                if (!(featureName === "PLUS_1" || featureName === "ENABLE_TRAIN" || featureName === "BJW")) {
                    PunkRocker.jumpWilds.playJumpAttentionHit(id);
                }
            });

            PREngineManager.getReel(conf.reelID).playLightning();
            if (featureName === "BJW") {
                SoundManager.playSoundEffect(SoundConfig.FS_LIGHTNING_BJW);
            } else {
                SoundManager.playSoundEffect(SoundConfig.FS_LIGHTNING);
            }

            if (onTrain === "top") {
                this.timeline.add([
                    PunkRocker.trainsManager.topTrain.playAttention(reelID, durr),
                    () => {
                        PunkRocker.trainsManager.topTrain.showGlowPositions([reelID], false, durr);
                    }
                ]);

            } else {
                this.timeline.add([
                    PunkRocker.trainsManager.bottomTrain.playAttention(reelID, durr),
                    () => {
                        PunkRocker.trainsManager.bottomTrain.showGlowPositions([reelID], false, durr);
                    }
                ]);
            }

            /*this.timeline.to(this, {
                duration: durr
            });*/
        }

        switch (featureName) {
            case "PLUS_1":
                this.FSUpCounter(jwIDs.length, true);
                jwIDs.forEach(id => {
                    this.timeline.add(PunkRocker.jumpWilds.PlayState(id, JumpWildState.FEATURE_PLUS_1_GET), 0);
                });
                break;

            case "MOLOTOV_FS":
                jwIDs.forEach(id => {
                    this.timeline.add(PunkRocker.jumpWilds.PlayState(id, JumpWildState.FEATURE_MOLOTOV_FS_GET), 0);
                });
                break;

            case "ENABLE_TRAIN":
                const topPosJWIdForTrain: number = jwIDs.length > 1 ? jwIDs[this.getTopPositionedJw([this._jumpPositions[jwIDs[0]], this._jumpPositions[jwIDs[1]]])] : jwIDs[0];
                PunkRocker.jumpWilds.PlayState(topPosJWIdForTrain, JumpWildState.FEATURE_ENABLE_TRAIN_GET);
                PunkRocker.noService.arriveBottomTrainOnly("ENABLE_TRAIN");
                break;

            case "BJW":
                jwIDs.forEach(id => {
                    this.timeline.add([
                        () => {
                            PunkRocker.jumpWilds.turnToBurning(id);
                        },
                        PunkRocker.jumpWilds.PlayState(id, JumpWildState.FEATURE_BJW_GET),
                    ], 0);
                });
                break;

            case "UPGRADE":
                this.FSUpCounter(2, false);
                jwIDs.forEach(id => {
                    this.timeline.add(PunkRocker.jumpWilds.PlayState(id, JumpWildState.FEATURE_UPGRADE_GET), 0);
                });
                break;

            case "6X6":
                this.showSpots();
                jwIDs.forEach(id => {
                    this.timeline.add(PunkRocker.jumpWilds.PlayState(id, JumpWildState.FEATURE_6X6_GET), 0);
                });
                break;

            case "XWAYS_FS":
                this.mysteryFlasher(reelID);
                jwIDs.forEach(id => {
                    this.timeline.add(PunkRocker.jumpWilds.PlayState(id, JumpWildState.FEATURE_XWAYS_FS_GET), 0);
                });
                break;

            case "X2":
            case "X3":
            case "X5":
            case "X10":
            case "X15":
            case "X25":
            case "X50":
            case "X75":
            case "X100":
                jwIDs.forEach(id => {
                    const currJWData: jumpingWildConfig = PunkRocker.model.gameData.raw.jumpingWildList[id],
                        reelId: number = currJWData.reelChosen;

                    if (this.featureWonTopTrain[reelId] === "UPGRADE") {
                        this.timeline.add(() => {
                            PunkRocker.jumpWilds.upgradeHandlerFromMerge(id, JumpWildState.FEATURE_MULT_GET);
                        }, 0);
                    } else {
                        this.timeline.add(PunkRocker.jumpWilds.PlayState(id, JumpWildState.FEATURE_MULT_GET), 0);
                    }

                    this.timeline.add(() => {
                        let updatedFeatureName: string;

                        if (onTrain === "top") {
                            const isBurningJW: boolean = PunkRocker.jumpWilds.isBurningJW(id);
                            if (isBurningJW) {
                                if (currJWData.previousMulti === 1) {
                                    updatedFeatureName = "X" + (parseInt(featureName.replace("X", "")));
                                } else {
                                    updatedFeatureName = "X" + (currJWData.previousMulti + parseInt(featureName.replace("X", "")));
                                }
                            } else {
                                updatedFeatureName = featureName;
                            }
                        } else {
                            updatedFeatureName = "X" + this._jumpPositions[id].multNew;
                        }

                        PunkRocker.jumpWilds.changeMultText(id, updatedFeatureName);
                    }, 0);
                });
                break;
        }

        /*this.timeline.to(this, {
            duration: 0.2
        });*/
    }

    // ----- Extra FreeSpins ------
    protected FSUpCounter(extraSpins: number, isFromPlusOne: boolean): void {
        const start: number = 0;
        this.fsUpdate += extraSpins;

        // #391
        this.timeline.add(() => {
            SoundManager.playSoundEffect(SoundConfig.FS_EXTRASPINS_ADDED);
        }, start);

        if (!isFromPlusOne) {
            this.timeline.add(() => PunkRocker.posterManager.playFSRetrigger(BigWinPosterState.RETRIGGER + "_" + extraSpins), start);
        }
        this.timeline.add(() => PunkRocker.keypadPlugin.setZeroBetSpinCounter(this.fsUpdate), start + 1 * 0.5);
    }

    // ----- 6x6 ------
    protected showSpots(): void {
        const startTime: number = this.timeline.duration();

        this.timeline.add(() => {
            PosterManager.instance.playSiren(true, 0);
            SoundManager.playSoundEffect(SoundConfig.SIREN);
        }, startTime);

        const sirenDurr: number = SpinePlayer.fetchDuration("Police", "PoliceFS_Loop_A"),
            waveDelay: number = 1;

        for (let times: number = 0; times < 3; times++) {
            for (let reelId: number = 0; reelId < PREngineManager.AllReels.length; reelId++) {
                this.timeline.add(() => {
                    PunkRocker.counter.triggerCountUp6x6(Math.pow((3 + times), 6), Math.pow((4 + times), 6), (waveDelay * 0.5));
                    /*PREngineManager.RAll(r => {
                        r.changeReelAmount(4 + times)
                    });*/
                    PREngineManager.AllReels[reelId].changeReelAmount(4 + times);
                    PunkRocker.reelEngineManager.flashBoxes(4 + times);
                }, (startTime + sirenDurr + (waveDelay * times)));
                this.timeline.to(this, {
                    duration: waveDelay
                }, (startTime + sirenDurr + (waveDelay * times)));
            }
            for (let jwId: number = 0; jwId < this._jumpPositions.length; jwId++) {
                if (this._jumpPositions[jwId].to.x !== -1) {
                    this.timeline.add(() => {
                        const xPos: number = this._jumpPositions[jwId].to.x,
                            yPos: number = (this.getUpdatedSymId(this._jumpPositions[jwId].to.y) + (times + 1));

                        PunkRocker.jumpWilds.updateSymbolPosFor6x6(jwId, xPos, yPos, MathHelper.frameToSeconds(20));
                        if (jwId === 0) {
                            SoundManager.playSoundEffect(SoundConfig.WILD_MOVES);
                        }
                    }, 0.2 + (startTime + sirenDurr + (waveDelay * times)));
                    this.timeline.to(this, {
                        duration: waveDelay
                    }, 0.2 + (startTime + sirenDurr + (waveDelay * times)));
                }
            }
        }

        this.timeline.add(() => {
            PosterManager.instance.playSiren(false);
            PunkRocker.counter.hideExpandCountUp();
            this._is6x6Done = true;
        });

        this.timeline.to(this, {
            duration: 0.15
        });
    }

    // ----- Mystery ------
    protected mysteryFlasher(reelID: number): void {
        const takenSpots: number[] = [];
        for (let jw: number = 0; jw < this._jumpPositions.length; jw++) {
            if (this._jumpPositions[jw].to.x == reelID) {
                for (let row: number = 0; row < PREngineManager.getReel(reelID).AllWindowSymbols.length; row++) {
                    if (this._jumpPositions[jw].to.y == row) takenSpots.push(row);
                }
            }
        }

        PREngineManager.getReel(reelID).AllWindowSymbols.forEach(s => {
            if (takenSpots.indexOf(s.posID[0]) == -1) {
                PunkRocker.overlayAnim.flashMystery(reelID, s.posID[0]);
            }
        });
    }

    protected unGlowReels(durr: number = 0.3): void {
        for (let reel: number = 0; reel < PREngineManager.AllReels.length; reel++) {
            PREngineManager.getReel(reel).overlayGraphicAlpha(0, (durr * 2));
            PREngineManager.getReel(reel).showReelGlow(false);
        }
    }

    protected trainArrives(): void {
        this.timeline.add(() => {
            PunkRocker.waysCounter.FreeSpinsCounter(0.4, false);
            PunkRocker.trainsManager.topTrain.setTrainState(TrainState.TRAIN_ENTER);
            PunkRocker.trainsManager.bottomTrain.setTrainState(TrainState.TRAIN_ENTER);
        });

        this.timeline.add(() => {
            if (!this.isSkipped) {
                PunkRocker.trainsManager.topTrain.trainLoop(true);
            }
        }, SpinePlayer.fetchDuration("topTrainSpine", TrainState.TRAIN_ENTER));

        this.timeline.add(() => {
            PunkRocker.trainsManager.bottomTrain.trainLoop(true);
        }, SpinePlayer.fetchDuration("bottomTrainSpine", TrainState.TRAIN_ENTER));

        this.timeline.add(() => {
            PunkRocker.waysCounter.counterActive(false);
        });
    }

    protected stopTopTrain(): void {
        PunkRocker.waysCounter.UpdateFreeSpinCounter(this.fsLeft - 1);

        const durationLeft: number = this.timeline.totalDuration() + PunkRocker.trainsManager.topTrain.timeLeftInLoop();

        this.timeline.add(() => {
            PunkRocker.trainsManager.topTrain.trainLoop(false);
            SoundManager.playSoundEffect(SoundConfig.TRAIN_BRAKE);
            PunkRocker.trainsManager.topTrain.setTrainState(this.isSkipped ? TrainState.TRAIN_OPEN_ONLY : TrainState.TRAIN_STOP_OPEN);
        }, durationLeft);

        this.timeline.add(() => {
            PunkRocker.trainsManager.topTrain.showTrainItems(true, this.jumpWReelPos, 0.3);
        }, durationLeft + PunkRocker.trainsManager.topTrain.getAnimDuration(TrainState.TRAIN_STOP_OPEN) - 0.15);

        for (let i: number = 0; i < this.jumpWReelPos.length; i++) {
            const reelIndex: number = this.jumpWReelPos[i];

            if (this.featureWonTopTrain[reelIndex] !== "BLANK") {
                this.timeline.add(PunkRocker.trainsManager.topTrain.showGlowPositions([reelIndex], true, 0.5), durationLeft + PunkRocker.trainsManager.topTrain.getAnimDuration(TrainState.TRAIN_STOP_OPEN) - 0.15);
            }
        }

        this.timeline.add(() => {
            this.resetSkip();
        });
    }

    protected stopBottomTrain(): void {
        if (PunkRocker.model.gameData.raw.bottomTrainEnabled) {
            const durationLeft: number = this.timeline.totalDuration() + PunkRocker.trainsManager.bottomTrain.timeLeftInLoop();

            this.timeline.add([
                () => {
                    PunkRocker.trainsManager.bottomTrain.trainLoop(false);
                    SoundManager.playSoundEffect(SoundConfig.TRAIN_BRAKE);
                },
                PunkRocker.trainsManager.bottomTrain.setTrainState(this.isSkipped ? TrainState.TRAIN_OPEN_ONLY : TrainState.TRAIN_STOP_OPEN)
            ], durationLeft);

            if (this._gameData.raw.burningTrain.includes(true)) {
                this.timeline.add(PunkRocker.trainsManager.bottomTrain.setTrainState(TrainState.TRAIN_OPEN_LOOP), (durationLeft + PunkRocker.trainsManager.bottomTrain.getAnimDuration(TrainState.TRAIN_STOP_OPEN)));
            }

            this.timeline.add(PunkRocker.trainsManager.bottomTrain.showTrainItems(true, this.jumpWReelPos, 0.3), (durationLeft + PunkRocker.trainsManager.bottomTrain.getAnimDuration(TrainState.TRAIN_STOP_OPEN) - 0.15))

            for (let i: number = 0; i < this.jumpWReelPos.length; i++) {
                const reelIndex: number = this.jumpWReelPos[i];

                if (this.featureWonBottomTrain[reelIndex] !== "BLANK") {
                    this.timeline.add(PunkRocker.trainsManager.bottomTrain.showGlowPositions([reelIndex], true, 0.5), durationLeft + PunkRocker.trainsManager.bottomTrain.getAnimDuration(TrainState.TRAIN_STOP_OPEN) - 0.15);
                }
            }
        }

        this.timeline.add(() => {
            this.resetSkip();
        });
    }

    protected jumpTheWilds(): void {
        PunkRocker.trainsManager.topTrain.setTrainItems(this.featureWonTopTrain);
        PunkRocker.trainsManager.bottomTrain.setTrainItems(this.featureWonBottomTrain);

        const startDelay: number = 0;
        this.timeline.add(() => {
            SoundManager.playSoundEffect(SoundConfig.WILD_MOVES)
        }, 0);
        for (let r = 0; r < this.jwBefore; r++) {
            this.timeline.add([
                PunkRocker.jumpWilds.JumpTo({
                    ID: r,
                    to: {reel: this._jumpPositions[r].to.x, row: this.getUpdatedSymId(this._jumpPositions[r].to.y)},
                    from: {reel: this._jumpPositions[r].from.x, row: this._jumpPositions[r].from.y},
                    duration: MathHelper.frameToSeconds(20),
                    state: JumpWildState.JUMP
                }),
                () => {
                    PunkRocker.jumpWilds.changeMult(r, 1);
                },
            ], startDelay)
        }
        this.timeline.add(() => {
            SoundManager.playSoundEffect(SoundConfig.WILD_LOCKS)
        }, startDelay + MathHelper.frameToSeconds(20));
        /*this.timeline.to(this, {
            duration: MathHelper.frameToSeconds(25)
        });*/
    }

    private resetSkip(): void {
        this.isSkipped = false;
        gsap.globalTimeline.timeScale(1);
        this.timeline.to(this, {
            duration: 0.15
        });
    }

    private getTopPositionedJw(jwArray: IStickyJump[]): number {
        return jwArray.reduce((minItem, currentItem, currentIndex, arr) => {
            if (currentItem.to.y < arr[minItem].to.y) {
                return currentIndex;
            } else {
                return minItem;
            }
        }, 0);
    }

    private getUpdatedSymId(symId: number): number {
        if (symId > 2) {
            if (symId === 3) {
                symId = 0;
            } else if (symId === 4) {
                symId = 1;
            } else {
                symId = 2;
            }
        }
        return symId;
    }
}